"use client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "store";
import { SkeletonTheme } from "react-loading-skeleton";
import React, { useEffect } from "react";
import { useAppSelector } from "appHooks";
import { configSelector } from "features/config/config.selectors";

const ThemeApplier: React.FC = () => {
  const { theme } = useAppSelector(configSelector);
  useEffect(() => {
    if (theme) {
      document.documentElement.setAttribute("data-theme", theme);
      try {
        localStorage.setItem("theme", theme);
      } catch {}
    }
  }, [theme]);
  return null;
};

const SkeletonThemeWrapper: React.FC<{
  children: React.ReactNode;
  locale: string;
}> = ({ children, locale }) => {
  // Read skeleton colors from CSS variables so SSR and CSR match
  const skeletonTheme = {
    baseColor: "var(--skeleton-base)",
    highlightColor: "var(--skeleton-highlight)",
  };
  return (
    <SkeletonTheme
      {...skeletonTheme}
      direction={locale === "ar" ? "rtl" : "ltr"}
    >
      {children}
    </SkeletonTheme>
  );
};

const Providers = ({
  children,
  locale,
}: {
  children: React.ReactNode;
  locale: string;
}) => {
  return (
    <Provider store={store}>
      {/* Wait for redux-persist to rehydrate the auth/config/client slices from
          localStorage before rendering. Without this gate, pages mount with an
          empty token on a hard refresh or deep link and immediately redirect to
          login (losing the user's place). Server and first client render both
          emit `null`, so there is no hydration mismatch. */}
      <PersistGate loading={null} persistor={persistor}>
        <SkeletonThemeWrapper locale={locale}>
          <ThemeApplier />
          {children}
        </SkeletonThemeWrapper>
      </PersistGate>
    </Provider>
  );
};

export default Providers;
